import { handleAuthError, isAuthError } from './utils/authUtils';
import { getCorrelationIdFromStorage } from './utils/correlationIdUtils';

import { TokenService } from '../application/services/TokenService';
import authServices from '../main/DependencyInjection';
import { AxiosInstance } from 'axios';
import { withScope, captureException } from '@sentry/react';

/**
 * Set up axios auth interceptors for handling authentication
 * @param tokenService The token service to use for retrieving/managing the token
 * @param axiosInstance The axios instance to attach interceptors to
 */
export const setupAxiosAuthInterceptors = (tokenService: TokenService, axiosInstance: AxiosInstance): void => {
  // Request interceptor to add authentication token and correlationId
  axiosInstance.interceptors.request.use(
    async (config) => {
      const token = tokenService.getToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      // Add correlationId header from storage
      const correlationId = getCorrelationIdFromStorage();
      if (correlationId) {
        config.headers['X-correlation-ID'] = correlationId;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle auth errors
  axiosInstance.interceptors.response.use(
    (response) => {
      // Check for auth errors in JSON response even with 200 status code
      // This handles cases where the backend returns auth errors with 200 OK
      const data = response.data;

      // Check for GraphQL-style auth errors
      if (data && data.errors && Array.isArray(data.errors)) {
        for (const err of data.errors) {
          withScope((scope) => {
            scope.setExtra('error_type', 'graphql-auth-error');
            captureException(err);
          });

          if (isAuthError(err)) {
            handleAuthError(authServices.authService);
            return Promise.reject(new Error('Authentication failed'));
          }
        }
      }
      return response;
    },
    (error) => {
      // Don't treat global settings API errors as auth errors that should log out the user
      const isTimezoneEndpoint = error.config?.url?.includes('default-timezone');
      const isTimeFormatEndpoint = error.config?.url?.includes('default-time-format');

      if (isTimezoneEndpoint || isTimeFormatEndpoint) {
        return Promise.reject(error);
      }

      // TROC-312: UM 403 must not clear the session (resource-server forbidden ≠ unauthenticated).
      const requestUrl = typeof error.config?.url === 'string' ? error.config.url : '';
      const isUserManagementForbidden = requestUrl.includes('/api/user-management/') && error.response?.status === 403;
      if (isUserManagementForbidden) {
        return Promise.reject(error);
      }

      // Public ERP landing (no Raptor JWT yet): 401 must stay on provision + Retry, not /login.
      const isPublicUmLandingAuth = requestUrl.includes('/auth/exchange-from-erp') || requestUrl.includes('/self-serve-provision/public');
      if (isPublicUmLandingAuth) {
        return Promise.reject(error);
      }

      if (isAuthError(error)) {
        handleAuthError(authServices.authService);
      }
      withScope((scope) => {
        scope.setExtra('error_type', 'axios-auth-error');
        captureException(error);
      });
      return Promise.reject(error);
    }
  );
};
